<template>
    <div class="w-full">
        <div>
            <div class="flex flex-col justify-between sm:flex-row px-3">
                <div class="mt-2 flex items-center">
                    <span class="mr-2">
                        {{ $t("common.posixToDateJson.outputDateFormat") }}
                    </span>
                    <radio-button
                        :isChecked="'local' === selectedOption"
                        @clickRadio="onInput('local')"
                        class="mr-2"
                        id="local"
                        :label="$t('common.posixToDateJson.local')"
                    />
                    <radio-button
                        :isChecked="'utc' === selectedOption"
                        @clickRadio="onInput('utc')"
                        class="mr-2"
                        id="utc"
                        :label="$t('common.posixToDateJson.utc')"
                    />
                </div>
            </div>

            <div v-if="error" class="my-3">
                <span class="text-red-700 text-lg">{{ error }}</span>
            </div>
            <span
                v-if="reasonForInvalidity"
                class="text-primary-primary dark:text-primary-secondary text-lg my-3 hidden md:block"
            >
                {{ $t("common.invalidJson") }} <br />
                {{ reasonForInvalidity }}
            </span>
        </div>

        <div class="flex flex-wrap justify-between">
            <div class="md:w-1/2 w-full px-3 mt-5">
                <div>
                    <span class="font-semibold text-lg">
                        {{ $t("common.posixToDateJson.jsonInput") }}
                    </span>
                </div>
                <code-editor v-model:value="input" />
            </div>

            <div class="w-full md:hidden flex justify-center mt-4 px-3">
                <button
                    :disabled="isProcessing"
                    @click="processFile"
                    class="flex items-center gap-2 border border-primary-primary hover:bg-white bg-primary-primary text-white hover:text-primary-primary font-bold py-2 px-6 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                    <span>
                        {{
                            isProcessing
                                ? $t("common.processing")
                                : $t("common.process")
                        }}
                    </span>
                </button>
            </div>

            <div
                id="posix-to-date-json-result"
                class="md:w-1/2 w-full px-3 mt-5"
            >
                <div class="flex flex-col">
                    <span class="font-semibold text-lg">
                        {{ $t("common.posixToDateJson.outputInput") }}
                    </span>
                    <span
                        v-if="reasonForInvalidity"
                        class="text-yellow-500 text-lg my-3 md:hidden"
                    >
                        {{ $t("common.invalidJson") }} {{ reasonForInvalidity }}
                    </span>
                </div>
                <code-editor v-model:value="result" read-only />
            </div>
        </div>

        <div class="hidden md:flex flex-col items-center px-3 mt-6 space-y-2">
            <button
                :disabled="isProcessing"
                @click="processFile"
                class="flex items-center gap-2 border border-primary-primary hover:bg-white bg-primary-primary text-white hover:text-primary-primary font-bold py-2 px-6 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
                <span>
                    {{
                        isProcessing
                            ? $t("common.processing")
                            : $t("common.process")
                    }}
                </span>
            </button>
            <span
                v-if="reasonForInvalidity"
                class="text-yellow-500 text-base text-center"
            >
                {{ $t("common.invalidJson") }}: {{ reasonForInvalidity }}
            </span>
        </div>
    </div>
</template>

<script lang="ts">
import type { DatesAdapter, JsonAdapter } from "inversify-utils-kit";
import { defineComponent, inject, reactive, toRefs } from "vue";

import CodeEditor from "@/components/utils/CodeEditor.vue";
import RadioButton from "@/components/utils/RadioButton.vue";

export default defineComponent({
    components: { CodeEditor, RadioButton },
    setup() {
        const datesAdapter = inject("datesAdapter") as DatesAdapter;
        const jsonAdapter = inject("jsonAdapter") as JsonAdapter;

        const state = reactive({
            input: `{
	"date": 1711930184
}`,
            result: `{
	"date": "2024-03-31T19:09:44.000-05:00"
}`,
            error: "",
            selectedOption: "local",
            reasonForInvalidity: "",
            isProcessing: false
        });

        const goToResultContainer = () => {
            const section = document.getElementById(
                "posix-to-date-json-result"
            );
            if (section) {
                section.scrollIntoView({ behavior: "smooth" });
            }
        };

        const processFile = async () => {
            console.log("Process file posix to date");
            state.isProcessing = true;
            state.reasonForInvalidity = "";
            state.result = "...";
            const jsonParsed = jsonAdapter.parseJsonStringToString(state.input);
            state.reasonForInvalidity = jsonParsed.reasonForInvalidity;
            if (state.reasonForInvalidity !== "") {
                state.input = jsonParsed.result;
                state.isProcessing = false;
                state.result = "error";
                return;
            }
            await new Promise((resolve) => setTimeout(resolve, 300));
            const result = jsonParsed.result.replace(
                /("|')?\d{10}(?:\d{3})?(?:\d{6})?(?:\d{9})?("|')?/g,
                (match: string) => {
                    try {
                        const matchClear = match.replaceAll(/("|')/g, "");
                        const { posix } = datesAdapter.fromUnix(matchClear);

                        const output =
                            state.selectedOption === "local"
                                ? datesAdapter.toLocal(posix)
                                : datesAdapter.toUTC(posix);
                        return `"${output}"`;
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    } catch (error) {
                        return match;
                    }
                }
            );
            state.result = result;
            goToResultContainer();
            state.isProcessing = false;
        };

        const onInput = (selected: string) => {
            state.selectedOption = selected;
        };
        return { processFile, onInput, ...toRefs(state) };
    }
});
</script>

<style scoped></style>
